import React from 'react';

import type { IntlShape } from 'react-intl';

import { invalidateBlockControlsSurfaces } from '@atlaskit/editor-common/block-controls/surface-candidate-invalidation';
import { convertToInlineCss } from '@atlaskit/editor-common/lazy-node-view';
import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Mark as PMMark, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import {
	PluginKey,
	type EditorState,
	type ReadonlyTransaction,
	type Transaction,
} from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { token } from '@atlaskit/tokens';

import type {
	AiSuggestionsPlugin,
	FollowUpCardState,
	Suggestion,
} from '../aiSuggestionsPluginType';
import { RemixPlaceholder } from '../ui/components/RemixPlaceholder';
import { SuggestionIconButton } from '../ui/components/SuggestionIconButton';
import { updateSuggestionStatus } from '../ui/utils/api';
import { applySuggestionDiff } from '../ui/utils/applySuggestionDiff';
import { buildSuggestionMaps } from '../ui/utils/buildSuggestionMaps';
import { FOLLOW_UP_CARD_ANCHOR_NAMES, shouldShowFollowUpCard } from '../ui/utils/followUpCard';
import { getAnchorEndLocalId, getAnchorLocalId } from '../ui/utils/getAnchorLocalId';
import { updateSuggestions } from '../ui/utils/updateSuggestions';
import { calculateHoverDecorations } from './hoverHighlightDecorations';
import { createRemixPlaceholderContainer, REMIX_PLACEHOLDER_WIDGET_KEY } from './remixPlaceholder';
import { getRemixSourceLocalIds } from './remixSuggestion';
import { getRemixWidgetPosition } from './remixWidgetPosition';
import { thinkingPluginKey } from './thinking-plugin';
import {
	findSuggestionCardAnchorRanges,
	type SuggestionCardAnchorRange,
} from './utils/cardAnchorRanges';
import {
	collectSuggestionIds,
	getCurrentSuggestions,
	getCurrentUndoDepth,
	getUndoDepthFromHistoryTr,
	getUndoRedoDirection,
} from './utils/undoRedo';

/**
 * We have different adjustments for aligned headings vs. non-aligned headings because
 * they have different margins for some reason, see platform/packages/editor/editor-common/src/styles/shared/headings.ts
 *
 * This is just hard coded at the moment to mimic the positioning logic of block controls plugin, and won't be needed
 * once the icon button positioning logic is moved to a new block controls plugin API.
 */
const nodeTopAdjustments: Record<string, number> = {
	table: 12,
	paragraph: 2,
	heading1: 5,
	heading2: 2,
	heading3: -1,
	heading4: -3,
	heading5: -3,
	heading6: -3,
	heading1_aligned: 5,
	heading2_aligned: 2,
	heading3_aligned: -1,
	heading4_aligned: -3,
	heading5_aligned: -3,
	heading6_aligned: -3,
	layoutSection: 8,
};

/**
 * It would be easier to get the margin from the DOM but we don't have access to the view / DOM here
 * so we hard code these values for now, until everything's moved to the block controls plugin.
 */
const nodeTopMargins: Record<string, number> = {
	table: 12,
	paragraph: 12,
	bulletList: 12,
	orderedList: 12,
	decisionList: 8,
	taskList: 12,
	codeBlock: 12,
	panel: 12,
	rule: 4,
	heading1: 40,
	heading2: 32,
	heading3: 24,
	heading4: 20,
	heading5: 20,
	heading6: 20,
	heading1_aligned: 45,
	heading2_aligned: 41,
	heading3_aligned: 36,
	heading4_aligned: 21,
	heading5_aligned: 22,
	heading6_aligned: 18,
	blockquote: 12,
	expand: 4,
};

/**
 * Bottom margins of nodes that affect how the icon button is positioned
 */
const nodeBeforeAdjustments: Record<string, number> = {
	rule: 24,
	extension: 12,
};

const constructHeadingAdjustmentKey = (node: PMNode): string => {
	if (node.type.name === 'heading') {
		const level = node.attrs.level;
		const hasAlignment = node.marks.some((mark) => mark.type.name === 'alignment');
		return `heading${level}${hasAlignment ? '_aligned' : ''}`;
	}
	return node.type.name;
};

/**
 * Anchor widget empty marks:
 * `marks: []` tells ProseMirror the widget belongs to no mark wrapper, so `syncToMarks` hoists it out of any
 * enclosing `breakout` wrapper instead of tucking it inside the next sibling's. Without it, a
 * widget with `side >= 0` placed before a breakout-marked sibling is inserted into that sibling's
 * (non-spanning) wrapper, and ProseMirror then destroys and re-creates the sibling and every
 * breakout-marked block after it on each update
 */
const ANCHOR_EMPTY_MARKS: readonly PMMark[] = [];

/**
 * How far apart `createCardAnchorDecorations` pushes its `side` pair:
 * - `-1`: the fallback pair (expand/table/row/layout boundary) — nothing else renders there.
 * - `1`: the primary (content) pair — wide enough to wrap any `editor-plugin-show-diff` widget
 *   sharing that position, since widgets there render in ascending `side` order.
 * - `0`: no bias, both widgets at `side: 0`.
 */
const CARD_ANCHOR_SIDE: Record<-1 | 0 | 1, { end: number; start: number }> = {
	[-1]: { end: 2, start: -2 },
	[0]: { end: 0, start: 0 },
	[1]: { end: 1000, start: -1000 },
};

/**
 * The paired start/end anchor widgets a card positions from, named `<startName>` / `<endName>`.
 *
 * Emitted twice per card — once for the primary range and once for the outward fallback (see
 * `findSuggestionCardAnchorRanges`) — so the consumer can chain them with `anchor()`'s fallback
 * argument.
 */
function createCardAnchorDecorations(
	names: { end: string; start: string },
	range: SuggestionCardAnchorRange,
	bias: -1 | 0 | 1 = -1,
): Decoration[] {
	const side = CARD_ANCHOR_SIDE[bias];
	return [
		Decoration.widget(range.startPos, () => createAnchorNameSpan(names.start), {
			side: side.start,
			// Stable key so rebuilding the DecorationSet (e.g. on SET_SUGGESTIONS from a
			// regeneration) reuses this span instead of remounting it — otherwise the CSS anchor
			// briefly disappears and the card flickers / mis-positions.
			key: names.start,
			marks: ANCHOR_EMPTY_MARKS,
		}),
		Decoration.widget(range.endPos, () => createAnchorNameSpan(names.end), {
			side: side.end,
			key: names.end,
			marks: ANCHOR_EMPTY_MARKS,
		}),
	];
}

/** An empty span carrying `anchor-name`, used as a CSS anchor target for the cards. */
function createAnchorNameSpan(anchorName: string): HTMLSpanElement {
	const spanElement = document.createElement('span');
	spanElement.setAttribute(
		'style',
		convertToInlineCss({
			anchorName,
		}),
	);
	return spanElement;
}

/**
 * Horizontal anchor for the follow-up card: a point in the right gutter of the removed
 * suggestion's block, where the suggestion card was anchored.
 *
 * The suggestion card anchors to the icon button's span (`SuggestionIconButton`), but that span
 * is empty whenever a card is open — the button hides while its suggestion is selected — so only
 * the gutter offset needs reproducing, not the button's size or alignment.
 */
function createFollowUpCardBaseAnchor(): HTMLSpanElement {
	// Full-width, zero-height block to offset the anchor from the content's right edge.
	const container = document.createElement('span');
	container.setAttribute(
		'style',
		convertToInlineCss({
			position: 'relative',
			width: '100%',
			display: 'block',
			pointerEvents: 'none',
		}),
	);
	const anchor = document.createElement('span');
	anchor.setAttribute(
		'style',
		convertToInlineCss({
			position: 'absolute',
			// gutter + object actions container width
			right: `calc(${token('space.negative.300')} + ${token('space.negative.300')})`,
			anchorName: FOLLOW_UP_CARD_ANCHOR_NAMES.base,
		}),
	);
	container.appendChild(anchor);
	return container;
}

/** Position and size of the first node in `doc` carrying `localId`. */
function findLocalIdNodePosition(
	doc: PMNode,
	localId: string | undefined,
): { nodeSize: number; pos: number } | undefined {
	if (!localId) {
		return undefined;
	}
	let result: { nodeSize: number; pos: number } | undefined;
	doc.descendants((node, pos) => {
		if (result !== undefined) {
			return false;
		}
		if (node.attrs?.localId === localId) {
			result = { pos, nodeSize: node.nodeSize };
			return false;
		}
		return true;
	});
	return result;
}

/**
 * Whether the selected suggestion's card can still be positioned against the document.
 * Runs on every doc-changing transaction while a card is open, so it avoids a full-document
 * traversal.
 */
function canPositionSelectedCard(doc: PMNode, pluginState: AiSuggestionsPluginState): boolean {
	const selectedId = pluginState.selectedSuggestion;
	if (!selectedId) {
		return true;
	}
	const suggestion = pluginState.suggestionById.get(selectedId);
	if (!suggestion) {
		return true;
	}

	const anchorLocalId = getAnchorLocalId(suggestion);
	const baseLocalId = pluginState.baseLocalIdBySuggestionId.get(selectedId);
	if (!anchorLocalId) {
		return true;
	}
	if (!baseLocalId) {
		return false;
	}

	let baseNode: PMNode | undefined;
	for (let index = 0; index < doc.childCount; index++) {
		const child = doc.child(index);
		if (child.attrs?.localId === baseLocalId) {
			baseNode = child;
			break;
		}
	}
	if (!baseNode) {
		return false;
	}
	if (anchorLocalId === baseLocalId) {
		return true;
	}

	// The anchor spans are only emitted from within the base node, so an anchor node that
	// survives elsewhere in the doc (moved out by a cut/paste, say) is just as unpositionable
	// as a deleted one.
	let hasAnchorDescendant = false;
	baseNode.descendants((descendant) => {
		if (hasAnchorDescendant) {
			return false;
		}
		if (descendant.attrs?.localId === anchorLocalId) {
			hasAnchorDescendant = true;
			return false;
		}
		return true;
	});
	return hasAnchorDescendant;
}

/**
 * Anchor spans for the follow-up card. Emitted for a suggestion that no longer exists, which is
 * the point: they keep the removed card's position addressable through resizes and reflow.
 *
 * Either localId can outlive the other (accepting a suggestion can delete the anchored node, or
 * the block the icon button sat on), so each anchor falls back to the other. With neither in the
 * doc no anchors are emitted and `position-visibility: anchors-valid` keeps the card hidden.
 */
function calculateFollowUpCardDecorations(
	doc: PMNode,
	followUpCard: FollowUpCardState | undefined,
): Decoration[] {
	if (!followUpCard) {
		return [];
	}

	const anchorNode = findLocalIdNodePosition(doc, followUpCard.anchorLocalId);
	const baseNode = findLocalIdNodePosition(doc, followUpCard.baseLocalId);
	const verticalNode = anchorNode ?? baseNode;
	const horizontalNode = baseNode ?? anchorNode;

	if (!verticalNode || !horizontalNode) {
		return [];
	}

	// Same treatment as the suggestion card: pin to the enclosing expand or table
	// row so the card lines up with the block the user actually sees.
	const anchorRanges = findSuggestionCardAnchorRanges(doc, verticalNode.pos);
	const primaryRange = anchorRanges.primary ?? {
		startPos: verticalNode.pos,
		endPos: verticalNode.pos + verticalNode.nodeSize,
	};

	return [
		...createCardAnchorDecorations(FOLLOW_UP_CARD_ANCHOR_NAMES, primaryRange, 1),
		...(anchorRanges.fallback
			? createCardAnchorDecorations(
					{
						start: FOLLOW_UP_CARD_ANCHOR_NAMES.outerStart,
						end: FOLLOW_UP_CARD_ANCHOR_NAMES.outerEnd,
					},
					anchorRanges.fallback,
				)
			: []),
		Decoration.widget(horizontalNode.pos, createFollowUpCardBaseAnchor, {
			side: -2,
			key: FOLLOW_UP_CARD_ANCHOR_NAMES.base,
			marks: ANCHOR_EMPTY_MARKS,
		}),
	];
}

export type AiSuggestionsPluginState = {
	baseLocalIdBySuggestionId: Map<string, string>;
	/**
	 * The content (page) the current suggestions belong to. Used to detect page
	 * navigation: when a new suggestion set arrives for a different `contentAri`,
	 * any selection/diff from the previous page is reset.
	 */
	contentAri: string | undefined;
	decorations: DecorationSet;
	/** The card shown in a removed suggestion card's position, if one qualified. */
	followUpCard: FollowUpCardState | undefined;
	/**
	 * Blue highlight decorations for the suggestion whose icon is currently
	 * hovered. Kept separate from `decorations` (which carries the icon widgets)
	 * so hovering never rebuilds — and therefore never remounts — the icon
	 * portals. Merged with `decorations` in the plugin's `decorations` prop.
	 */
	hoverDecorations: DecorationSet;
	hoveredSuggestionId: string | undefined;
	isHidden: boolean;
	/**
	 * `decorations` merged with `hoverDecorations`, precomputed in `apply` so the
	 * `decorations` prop can return a stable set rather than allocating a new
	 * merged `DecorationSet` on every ProseMirror update while a hover is active.
	 * Equals `decorations` by reference when there is no hover highlight.
	 */
	mergedDecorations: DecorationSet;
	orderedSuggestionIds: string[];
	/** Groups an undo restored that a redo can remove again (newest last); cleared on a fresh accept. */
	redoableAcceptedSuggestions: AcceptedSuggestionGroup[];
	/**
	 * Ids of suggestions currently being regenerated (stale-check → regenerate),
	 * triggered on select.
	 */
	regeneratingSuggestionIds: Set<string>;
	/**
	 * Placeholder marking where a selected `SUGGESTED_REMIX` result will land. Kept
	 * separate from `decorations`/`hoverDecorations` so it survives the sparkle UI
	 * being hidden (sidebar open) or another suggestion being hovered.
	 */
	remixPlaceholderDecorations: DecorationSet;
	selectedSuggestion: string | undefined;
	/**
	 * Retained node highlight for the selected suggestion, when its suggestion
	 * type supports one (informational insights and suggested remixes). This is
	 * intentionally independent from `hoverDecorations`: Confluence hides the
	 * sparkle UI while its suggestions sidebar is open, but the selected
	 * suggestion must remain highlighted in the editor.
	 */
	selectedSuggestionDecorations: DecorationSet;
	suggestionById: Map<string, Suggestion>;
	suggestionIdsByBaseLocalId: Map<string, Set<string>>;
	/** LIFO stack of accepted groups (newest last) that a native undo restores; inert without accepts. */
	undoableAcceptedSuggestions: AcceptedSuggestionGroup[];
};

/** Suggestions accepted in one undoable step, tagged with the history depth used to pair undo/redo. */
export type AcceptedSuggestionGroup = {
	suggestions: Suggestion[];
	/** History depth (`history$` `done.eventCount`) at accept: undo below it reverts, redo back to it re-applies. */
	undoDepthAtAccept: number;
};

export const aiSuggestionsPluginKey: PluginKey<AiSuggestionsPluginState> =
	new PluginKey<AiSuggestionsPluginState>('aiSuggestionsPlugin');

export type AiSuggestionsMeta =
	| { action: 'SET_SUGGESTIONS'; contentAri?: string; suggestions: Suggestion[] }
	| { action: 'SET_REGENERATING'; ids: Set<string> }
	| { action: 'CLEAR_SUGGESTIONS' }
	| { action: 'REMOVE_SUGGESTIONS'; ids: string[] }
	| { action: 'SELECT_SUGGESTION'; id: string | undefined }
	| { action: 'REMOVE_SUGGESTION'; id: string }
	| { action: 'ACCEPT_SUGGESTION'; suggestion: Suggestion }
	| { action: 'DISMISS_FOLLOW_UP_CARD' }
	| { action: 'SET_UI_VISIBILITY'; isHidden: boolean }
	| { action: 'HOVER_SUGGESTION'; id: string | undefined };

/** Returns cached base-block ids affected by suggestion surface state changes. */
const getSuggestionSurfaceInvalidationIds = (
	transactions: readonly Transaction[],
	oldState: EditorState,
	newState: EditorState,
): readonly string[] => {
	const metas = transactions
		.map(
			(transaction) => transaction.getMeta(aiSuggestionsPluginKey) as AiSuggestionsMeta | undefined,
		)
		.filter((meta): meta is AiSuggestionsMeta => meta !== undefined);
	if (metas.length === 0) {
		return [];
	}

	const changesSurfaceVisibility = metas.some(
		(meta) => meta.action !== 'SET_REGENERATING' && meta.action !== 'DISMISS_FOLLOW_UP_CARD',
	);
	if (!changesSurfaceVisibility) {
		return [];
	}

	// Use both snapshots so removals and base-local-id remapping invalidate the previous anchors too.
	const ids = new Set([
		...(aiSuggestionsPluginKey.getState(oldState)?.suggestionIdsByBaseLocalId.keys() ?? []),
		...(aiSuggestionsPluginKey.getState(newState)?.suggestionIdsByBaseLocalId.keys() ?? []),
	]);
	return [...ids];
};

type DocRange = {
	from: number;
	to: number;
};

const pointTouchesRange = (point: number, range: DocRange): boolean =>
	point >= range.from && point <= range.to;

const rangesOverlap = (rangeA: DocRange, rangeB: DocRange): boolean => {
	if (rangeA.from === rangeA.to) {
		return pointTouchesRange(rangeA.from, rangeB);
	}

	if (rangeB.from === rangeB.to) {
		return pointTouchesRange(rangeB.from, rangeA);
	}

	return rangeA.from < rangeB.to && rangeB.from < rangeA.to;
};

const getDecoratedNodeRanges = (
	doc: PMNode,
	suggestionIdsByBaseLocalId: Map<string, Set<string>>,
): DocRange[] => {
	const decoratedNodeRanges: DocRange[] = [];

	doc.descendants((node, pos) => {
		if (suggestionIdsByBaseLocalId.has(node.attrs?.localId)) {
			decoratedNodeRanges.push({ from: pos, to: pos + node.nodeSize });
			return false;
		}

		return true;
	});

	return decoratedNodeRanges;
};

const docChangeAffectsDecorations = ({
	doc,
	suggestionIdsByBaseLocalId,
	tr,
}: {
	doc: PMNode;
	suggestionIdsByBaseLocalId: Map<string, Set<string>>;
	tr: ReadonlyTransaction;
}): boolean => {
	if (!tr.docChanged || suggestionIdsByBaseLocalId.size === 0) {
		return false;
	}

	const decoratedNodeRanges = getDecoratedNodeRanges(doc, suggestionIdsByBaseLocalId);
	if (decoratedNodeRanges.length === 0) {
		return false;
	}

	return tr.mapping.maps.some((stepMap) => {
		let affectsDecorations = false;

		stepMap.forEach((oldStart, oldEnd) => {
			if (affectsDecorations) {
				return;
			}

			const changedRange = { from: oldStart, to: oldEnd };
			affectsDecorations = decoratedNodeRanges.some((decoratedRange) =>
				rangesOverlap(changedRange, decoratedRange),
			);
		});

		return affectsDecorations;
	});
};

function calculateSuggestionsDecorations({
	doc,
	followUpCard,
	suggestionById,
	suggestionIdsByBaseLocalId,
	api,
	intl,
	selectedSuggestion,
	nodeViewPortalProviderAPI,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	doc: PMNode;
	followUpCard: FollowUpCardState | undefined;
	intl: IntlShape | undefined;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	selectedSuggestion: string | undefined;
	suggestionById: Map<string, Suggestion>;
	suggestionIdsByBaseLocalId: Map<string, Set<string>>;
}): DecorationSet {
	// The follow-up card outlives the suggestion it replaces, so its anchors are emitted
	// independently of `suggestionById` — including when that map is empty, which is exactly when
	// the card shows.
	const followUpCardDecorations = calculateFollowUpCardDecorations(doc, followUpCard);

	if (suggestionById.size === 0) {
		return followUpCardDecorations.length > 0
			? DecorationSet.create(doc, followUpCardDecorations)
			: DecorationSet.empty;
	}

	const decorations: Decoration[] = [...followUpCardDecorations];

	doc.descendants((node, pos) => {
		const { localId } = node.attrs;
		if (localId) {
			const suggestionsAtLocalId = suggestionIdsByBaseLocalId.get(localId);
			if (suggestionsAtLocalId?.size) {
				const firstSuggestionId = suggestionsAtLocalId.values().next().value;
				const firstSuggestion = firstSuggestionId
					? suggestionById.get(firstSuggestionId)
					: undefined;

				if (firstSuggestion) {
					const element = document.createElement('span');
					const nodeTypeName =
						node.type.name === 'heading' ? constructHeadingAdjustmentKey(node) : node.type.name;
					element.setAttribute(
						'style',
						convertToInlineCss({
							position: 'relative',
							width: '100%',
							display: 'block',
							pointerEvents: 'none',
						}),
					);

					const isSelected = Boolean(
						selectedSuggestion && suggestionsAtLocalId.has(selectedSuggestion),
					);

					const isTableNode = node.type.name === 'table';

					// Tables don't use breakout for resizing, the width is stored in the attrs
					// but will be clamped by the max content width (100cqw)
					// The (-4px) is mimicking spacing of the remix icon button in block controls plugin
					const leftAdjustment =
						isTableNode && typeof node.attrs?.width === 'number'
							? `calc(((min(100cqw - (72px * 2), ${node.attrs.width}px) - 760px) / 2) - 4px)`
							: undefined;

					const nodeBefore = doc.resolve(pos).nodeBefore;
					const isBlockLeafNodeBefore = nodeBefore?.isBlock && nodeBefore.isLeaf;

					const topAdjustment = nodeTopAdjustments[nodeTypeName] ?? 0;
					const topMargin = nodeTopMargins[nodeTypeName] ?? 0;
					const nodeBeforeAdjustment = nodeBeforeAdjustments[nodeBefore?.type.name ?? ''] ?? 0;

					const calculatedTopAdjustment =
						(pos === 0
							? isTableNode
								? topAdjustment
								: 0
							: isBlockLeafNodeBefore
								? Math.max(0, topMargin - nodeBeforeAdjustment)
								: topMargin) + topAdjustment;

					/**
					 * Stable key so rebuilding the DecorationSet (e.g. a SELECT_SUGGESTION, or a
					 * SET_SUGGESTIONS from a regeneration) reuses this widget instead of replacing
					 * its DOM. The widget hosts the `--button-<localId>` anchor the card is
					 * positioned from, and the portal that renders it is flushed on a throttled
					 * update — so recreating the element left a window on every rebuild where the
					 * anchor resolved to nothing, and anything measuring the card's anchor position
					 * in that window (see `useSuggestionCardPositioning`) read a static-position
					 * fallback instead of the button's real position.
					 *
					 * The geometry inputs are part of the key because ProseMirror skips `toDOM` for
					 * a reused widget, so a stale `leftAdjustment` / `topAdjustment` could not be
					 * corrected by a re-render. Everything else `SuggestionIconButton` needs it
					 * reads from plugin state itself, so selection and suggestion changes re-render
					 * the button without disturbing the anchor.
					 */
					const key = `--button-${localId}|${calculatedTopAdjustment}|${leftAdjustment ?? ''}`;

					// EDITOR-8771: the icon button is registered via the UI Control Registry API
					// (see `SuggestionRightSurfaceButton`/`aiSuggestionsPlugin.tsx`) once the migration
					// gate is on, so skip the legacy widget here to avoid rendering it twice. Every
					// other decoration below (card anchors, remix placeholder) is unrelated to which
					// system renders the icon button and must still run regardless of this gate.
					if (!isExperimentEnabled('platform_editor_block_control_migration')) {
						decorations.push(
							Decoration.widget(
								pos,
								() => {
									nodeViewPortalProviderAPI.render(
										() => (
											<SuggestionIconButton
												leftAdjustment={leftAdjustment}
												topAdjustment={calculatedTopAdjustment}
												localId={localId}
												api={api}
												intl={intl}
											/>
										),
										element,
										key,
									);
									return element;
								},
								{
									side: -2,
									key,
									marks: node.marks.filter((mark) => mark.type.name === 'breakout'),
									destroy: () => {
										// Clean up properly
										// nodeViewPortalProviderAPI.remove(key);
									},
								},
							),
						);
					}

					if (isSelected) {
						const selectedSuggestionData = selectedSuggestion
							? suggestionById.get(selectedSuggestion)
							: undefined;
						const anchorLocalId = selectedSuggestionData
							? getAnchorLocalId(selectedSuggestionData)
							: undefined;
						const anchorEndLocalId = selectedSuggestionData
							? getAnchorEndLocalId(selectedSuggestionData)
							: anchorLocalId;

						// The remix placeholder is emitted separately (see
						// `calculateRemixPlaceholderDecorations`).

						if (anchorLocalId) {
							let anchorNodePosition: { nodeSize: number; pos: number } | undefined;
							if (anchorLocalId === localId) {
								anchorNodePosition = { pos, nodeSize: node.nodeSize };
							} else {
								node.descendants((descendant, descendantPos) => {
									if (anchorNodePosition) {
										return false;
									}
									const descendantLocalId = descendant.attrs?.localId;
									if (descendantLocalId === anchorLocalId) {
										anchorNodePosition = {
											pos: pos + descendantPos + 1,
											nodeSize: descendant.nodeSize,
										};
										return false;
									}
									return true;
								});
							}

							const innerCardStartPos = anchorNodePosition?.pos ?? pos;
							const innerCardStartEndPos = anchorNodePosition
								? anchorNodePosition.pos + anchorNodePosition.nodeSize
								: pos + node.nodeSize;
							const innerCardEndNode = findLocalIdNodePosition(doc, anchorEndLocalId);

							// Pin the card anchors to the enclosing expand or table row so the spans
							// render at that block's boundary rather than deep inside it, keeping the
							// card aligned with the row the user sees while staying out of the grid
							// `<tr>` (EDITOR-8442). See `findSuggestionCardAnchorRanges`.
							const anchorRanges = findSuggestionCardAnchorRanges(doc, innerCardStartPos);
							const startPrimaryRange = anchorRanges.primary ?? {
								startPos: innerCardStartPos,
								endPos: innerCardStartEndPos,
							};
							const endAnchorRanges = innerCardEndNode
								? findSuggestionCardAnchorRanges(doc, innerCardEndNode.pos)
								: anchorRanges;
							const endPrimaryRange =
								endAnchorRanges.primary ??
								(innerCardEndNode
									? {
											startPos: innerCardEndNode.pos,
											endPos: innerCardEndNode.pos + innerCardEndNode.nodeSize,
										}
									: startPrimaryRange);

							const primaryRange = {
								startPos: startPrimaryRange.startPos,
								endPos: endPrimaryRange.endPos,
							};

							/**
							 * These decoration widgets are used to track the top and bottom of the localId,
							 * also accounting for other decoration widgets (such as diffs).
							 */
							decorations.push(
								...createCardAnchorDecorations(
									{
										start: `--card-${anchorLocalId}-start`,
										end: `--card-${anchorEndLocalId}-end`,
									},
									primaryRange,
									1,
								),
							);

							// Only emitted when the primary range sits inside the target, i.e. where the
							// diff can hide it. The card chains the two with `anchor()`'s fallback
							// argument, so these are inert while the primary anchors are rendered.
							const startFallbackRange = anchorRanges.fallback;
							const endFallbackRange = endAnchorRanges.fallback;
							if (startFallbackRange || endFallbackRange) {
								decorations.push(
									...createCardAnchorDecorations(
										{
											start: `--card-${anchorLocalId}-outer-start`,
											end: `--card-${anchorEndLocalId}-outer-end`,
										},
										{
											startPos: startFallbackRange?.startPos ?? primaryRange.startPos,
											endPos: endFallbackRange?.endPos ?? primaryRange.endPos,
										},
									),
								);
							}
						}
					}
				}
			}
		}
		return false;
	});

	if (!decorations.length) {
		return DecorationSet.empty;
	}

	return DecorationSet.create(doc, decorations);
}

// Whether a `SUGGESTED_REMIX` card is selected; gates the manual placeholder-portal teardown.
function isSuggestedRemixCardActive(pluginState: AiSuggestionsPluginState | undefined): boolean {
	if (!pluginState?.selectedSuggestion) {
		return false;
	}
	return (
		pluginState.suggestionById.get(pluginState.selectedSuggestion)?.actionKind === 'SUGGESTED_REMIX'
	);
}

function shouldRetainSelectedSuggestionHighlight(suggestion: Suggestion | undefined): boolean {
	return suggestion?.actionKind === 'INFORMATIONAL' || suggestion?.actionKind === 'SUGGESTED_REMIX';
}

function getSelectedSuggestionForHighlight(
	pluginState: AiSuggestionsPluginState,
): Suggestion | undefined {
	const selectedSuggestion = pluginState.selectedSuggestion
		? pluginState.suggestionById.get(pluginState.selectedSuggestion)
		: undefined;
	return shouldRetainSelectedSuggestionHighlight(selectedSuggestion)
		? selectedSuggestion
		: undefined;
}

function calculateSelectedSuggestionDecorations(
	doc: PMNode,
	pluginState: AiSuggestionsPluginState,
	isEditorInViewMode: boolean,
): DecorationSet {
	return isEditorInViewMode
		? DecorationSet.empty
		: calculateHoverDecorations(doc, getSelectedSuggestionForHighlight(pluginState));
}

/**
 * Placeholder marking where a selected `SUGGESTED_REMIX` result will land. Emitted
 * independently of the sparkle-UI `decorations`, mirroring
 * `calculateSelectedSuggestionDecorations`.
 */
function calculateRemixPlaceholderDecorations(
	doc: PMNode,
	pluginState: AiSuggestionsPluginState,
	isEditorInViewMode: boolean,
	nodeViewPortalProviderAPI: PortalProviderAPI,
): DecorationSet {
	if (isEditorInViewMode) {
		return DecorationSet.empty;
	}

	const selectedSuggestionData = pluginState.selectedSuggestion
		? pluginState.suggestionById.get(pluginState.selectedSuggestion)
		: undefined;

	if (selectedSuggestionData?.actionKind !== 'SUGGESTED_REMIX') {
		return DecorationSet.empty;
	}

	// Drop the placeholder after the last source localId (falling back to the anchor).
	const placeholderPos = getRemixWidgetPosition(
		doc,
		getRemixSourceLocalIds(selectedSuggestionData),
	);
	if (placeholderPos === undefined) {
		return DecorationSet.empty;
	}

	const placeholderElement = createRemixPlaceholderContainer();
	// No `destroy`; the portal is removed manually when the card goes inactive (see plugin `view`).
	return DecorationSet.create(doc, [
		Decoration.widget(
			placeholderPos,
			() => {
				nodeViewPortalProviderAPI.render(
					() => <RemixPlaceholder />,
					placeholderElement,
					REMIX_PLACEHOLDER_WIDGET_KEY,
				);
				return placeholderElement;
			},
			{
				key: REMIX_PLACEHOLDER_WIDGET_KEY,
				side: -1,
			},
		),
	]);
}

/**
 * Rebuild the suggestion-driven slice of plugin state (maps + decorations) for a given
 * set of suggestions against `doc`. Shared by accept (removing cards) and undo/redo
 * (restoring/removing cards) so they all produce a consistent state and decorations.
 */
function rebuildSuggestionsState({
	pluginState,
	suggestions,
	doc,
	suggestionsHidden,
	api,
	intl,
	nodeViewPortalProviderAPI,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	doc: PMNode;
	intl?: IntlShape;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	pluginState: AiSuggestionsPluginState;
	suggestions: Suggestion[];
	suggestionsHidden: boolean;
}): AiSuggestionsPluginState {
	const {
		suggestionById,
		baseLocalIdBySuggestionId,
		suggestionIdsByBaseLocalId,
		orderedSuggestionIds,
	} = buildSuggestionMaps({ doc, suggestions });

	return {
		...pluginState,
		suggestionById,
		baseLocalIdBySuggestionId,
		suggestionIdsByBaseLocalId,
		orderedSuggestionIds,
		selectedSuggestion: undefined,
		followUpCard: undefined,
		hoveredSuggestionId: undefined,
		hoverDecorations: DecorationSet.empty,
		selectedSuggestionDecorations: DecorationSet.empty,
		decorations: suggestionsHidden
			? DecorationSet.empty
			: calculateSuggestionsDecorations({
					doc,
					followUpCard: undefined,
					suggestionById,
					suggestionIdsByBaseLocalId,
					api,
					intl,
					selectedSuggestion: undefined,
					nodeViewPortalProviderAPI,
				}),
	};
}

const {
	suggestionById: initSuggestionById,
	suggestionIdsByBaseLocalId: initSuggestionIdsByBaseLocalId,
	baseLocalIdBySuggestionId: initBaseLocalIdBySuggestionId,
	orderedSuggestionIds: initOrderedSuggestionIds,
} = buildSuggestionMaps(undefined);

const initState: AiSuggestionsPluginState = {
	contentAri: undefined,
	decorations: DecorationSet.empty,
	followUpCard: undefined,
	hoverDecorations: DecorationSet.empty,
	hoveredSuggestionId: undefined,
	mergedDecorations: DecorationSet.empty,
	isHidden: false,
	selectedSuggestion: undefined,
	suggestionById: initSuggestionById,
	suggestionIdsByBaseLocalId: initSuggestionIdsByBaseLocalId,
	baseLocalIdBySuggestionId: initBaseLocalIdBySuggestionId,
	orderedSuggestionIds: initOrderedSuggestionIds,
	regeneratingSuggestionIds: new Set<string>(),
	selectedSuggestionDecorations: DecorationSet.empty,
	remixPlaceholderDecorations: DecorationSet.empty,
	undoableAcceptedSuggestions: [],
	redoableAcceptedSuggestions: [],
};

// View mode is a hard override for the suggestions UI: the document is read-only, so a suggestion
// can't be meaningfully accepted/discarded. Checked at the source so it can't be undone by a stray
// `setUIVisibility(false)`.
const isViewMode = (api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined): boolean =>
	api?.editorViewMode?.sharedState.currentState()?.mode === 'view';

export function createPlugin({
	api,
	intl,
	contentAri,
	nodeViewPortalProviderAPI,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	contentAri?: string;
	intl?: IntlShape;
	nodeViewPortalProviderAPI: PortalProviderAPI;
}): SafePlugin<AiSuggestionsPluginState> {
	return new SafePlugin<AiSuggestionsPluginState>({
		key: aiSuggestionsPluginKey,
		state: {
			init(_config, _state: EditorState): AiSuggestionsPluginState {
				return initState;
			},
			apply(
				tr: ReadonlyTransaction,
				pluginState: AiSuggestionsPluginState,
				_oldState: EditorState,
				newState: EditorState,
			): AiSuggestionsPluginState {
				const meta = tr.getMeta(aiSuggestionsPluginKey) as AiSuggestionsMeta | undefined;
				let nextState = pluginState;

				// View mode is a hard override: suggestions UI must never be shown while read-only,
				// regardless of the `isHidden` flag (which can be toggled by `setUIVisibility`).
				const suggestionsHidden = pluginState.isHidden || isViewMode(api);

				if (meta) {
					switch (meta.action) {
						case 'SET_SUGGESTIONS': {
							const {
								suggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds,
							} = buildSuggestionMaps({
								suggestions: meta.suggestions,
								doc: newState.doc,
							});

							// Track the content these suggestions belong to. Prefer the explicit
							// `contentAri` on the meta, falling back to the suggestions' own
							// `contentAri` so direct dispatchers are covered too. `||` (not `??`)
							// is intentional so an empty-string `contentAri` is treated as "not
							// provided" rather than a distinct page (which would cause a spurious
							// reset against a previously-tracked ARI).
							const incomingContentAri = meta.contentAri || meta.suggestions[0]?.contentAri;
							// When the suggestions belong to a different page than the one currently
							// tracked, the selection (and its diff) is stale — reset it. Suggestions
							// refreshed for the *same* page keep the existing selection.
							const contentChanged =
								incomingContentAri !== undefined &&
								pluginState.contentAri !== undefined &&
								incomingContentAri !== pluginState.contentAri;
							const nextSelectedSuggestion = contentChanged
								? undefined
								: pluginState.selectedSuggestion;
							// A follow-up card belongs to the page whose suggestion was removed.
							const nextFollowUpCard = contentChanged ? undefined : pluginState.followUpCard;

							nextState = {
								...pluginState,
								contentAri: incomingContentAri ?? pluginState.contentAri,
								selectedSuggestion: nextSelectedSuggestion,
								followUpCard: nextFollowUpCard,
								suggestionById,
								suggestionIdsByBaseLocalId,
								baseLocalIdBySuggestionId,
								orderedSuggestionIds,
								hoverDecorations: DecorationSet.empty,
								hoveredSuggestionId: undefined,
								decorations: suggestionsHidden
									? DecorationSet.empty
									: calculateSuggestionsDecorations({
											doc: newState.doc,
											followUpCard: nextFollowUpCard,
											suggestionById,
											suggestionIdsByBaseLocalId,
											api,
											intl,
											selectedSuggestion: nextSelectedSuggestion,
											nodeViewPortalProviderAPI,
										}),
							};
							break;
						}
						case 'CLEAR_SUGGESTIONS': {
							const {
								suggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds,
							} = buildSuggestionMaps({
								doc: newState.doc,
								suggestions: [],
							});
							nextState = {
								...pluginState,
								suggestionById,
								suggestionIdsByBaseLocalId,
								baseLocalIdBySuggestionId,
								orderedSuggestionIds,
								selectedSuggestion: undefined,
								followUpCard: undefined,
								decorations: DecorationSet.empty,
								hoverDecorations: DecorationSet.empty,
								selectedSuggestionDecorations: DecorationSet.empty,
								hoveredSuggestionId: undefined,
							};
							break;
						}
						case 'REMOVE_SUGGESTIONS': {
							const removedSuggestionIds = new Set(meta.ids);
							const remainingSuggestions = pluginState.orderedSuggestionIds
								.filter((id) => !removedSuggestionIds.has(id))
								.map((id) => pluginState.suggestionById.get(id))
								.filter((suggestion): suggestion is Suggestion => suggestion !== undefined);
							const {
								suggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds,
							} = buildSuggestionMaps({
								doc: newState.doc,
								suggestions: remainingSuggestions,
							});
							nextState = {
								...pluginState,
								suggestionById,
								suggestionIdsByBaseLocalId,
								baseLocalIdBySuggestionId,
								orderedSuggestionIds,
								selectedSuggestion: undefined,
								followUpCard: undefined,
								decorations: suggestionsHidden
									? DecorationSet.empty
									: calculateSuggestionsDecorations({
											doc: newState.doc,
											followUpCard: undefined,
											suggestionById,
											suggestionIdsByBaseLocalId,
											api,
											intl,
											selectedSuggestion: undefined,
											nodeViewPortalProviderAPI,
										}),
								hoverDecorations: DecorationSet.empty,
								selectedSuggestionDecorations: DecorationSet.empty,
								hoveredSuggestionId: undefined,
							};
							break;
						}
						case 'SET_REGENERATING': {
							nextState = {
								...pluginState,
								regeneratingSuggestionIds: new Set(meta.ids),
							};
							break;
						}
						case 'SELECT_SUGGESTION': {
							// Opening a suggestion card supersedes the follow-up card, so both are never on
							// screen at once. Deselecting leaves it alone, so a stray
							// `selectSuggestion(null)` can't close it.
							const nextFollowUpCard = meta.id ? undefined : pluginState.followUpCard;
							nextState = {
								...pluginState,
								selectedSuggestion: meta.id,
								followUpCard: nextFollowUpCard,
								hoverDecorations: DecorationSet.empty,
								hoveredSuggestionId: undefined,
								decorations: suggestionsHidden
									? DecorationSet.empty
									: calculateSuggestionsDecorations({
											doc: newState.doc,
											followUpCard: nextFollowUpCard,
											suggestionById: pluginState.suggestionById,
											suggestionIdsByBaseLocalId: pluginState.suggestionIdsByBaseLocalId,
											api,
											intl,
											selectedSuggestion: meta.id,
											nodeViewPortalProviderAPI,
										}),
							};
							break;
						}
						case 'SET_UI_VISIBILITY': {
							// Persist the requested `isHidden`, but gate the actual UI on view mode too so a
							// `setUIVisibility(false)` can never reveal suggestions while read-only.
							const hidden = meta.isHidden || isViewMode(api);
							nextState = {
								...pluginState,
								isHidden: meta.isHidden,
								// Hiding the suggestions UI closes the card for good, rather than parking it
								// to reappear later out of context.
								followUpCard: hidden ? undefined : pluginState.followUpCard,
								hoverDecorations: hidden ? DecorationSet.empty : pluginState.hoverDecorations,
								hoveredSuggestionId: hidden ? undefined : pluginState.hoveredSuggestionId,
								decorations: hidden
									? DecorationSet.empty
									: calculateSuggestionsDecorations({
											doc: newState.doc,
											followUpCard: pluginState.followUpCard,
											suggestionById: pluginState.suggestionById,
											suggestionIdsByBaseLocalId: pluginState.suggestionIdsByBaseLocalId,
											api,
											intl,
											selectedSuggestion: pluginState.selectedSuggestion,
											nodeViewPortalProviderAPI,
										}),
							};
							break;
						}
						case 'ACCEPT_SUGGESTION': {
							const acceptedSuggestions = [meta.suggestion];
							const acceptedIds = new Set(acceptedSuggestions.map(({ id }) => id));
							const remainingSuggestions = getCurrentSuggestions(pluginState).filter(
								(suggestion) => !acceptedIds.has(suggestion.id),
							);

							const acceptedGroup: AcceptedSuggestionGroup = {
								suggestions: acceptedSuggestions,
								undoDepthAtAccept: getCurrentUndoDepth(api) + 1,
							};

							nextState = rebuildSuggestionsState({
								pluginState,
								suggestions: remainingSuggestions,
								doc: newState.doc,
								suggestionsHidden,
								api,
								intl,
								nodeViewPortalProviderAPI,
							});
							nextState = {
								...nextState,
								undoableAcceptedSuggestions: [
									...pluginState.undoableAcceptedSuggestions,
									acceptedGroup,
								],
								redoableAcceptedSuggestions: [],
							};
							break;
						}
						case 'REMOVE_SUGGESTION': {
							const {
								suggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds,
							} = pluginState;
							const removedId = meta.id;
							const suggestion = suggestionById.get(removedId);

							if (!suggestion) {
								nextState = {
									...pluginState,
									decorations: calculateSuggestionsDecorations({
										doc: newState.doc,
										followUpCard: pluginState.followUpCard,
										suggestionById,
										suggestionIdsByBaseLocalId,
										api,
										intl,
										selectedSuggestion: undefined,
										nodeViewPortalProviderAPI,
									}),
								};
								break;
							}

							const nextOrderedSuggestionIds = orderedSuggestionIds.filter(
								(id) => id !== removedId,
							);
							// Only the last suggestion leaves a follow-up card behind — while others remain
							// the user carries on reviewing them. Captured before the maps are pruned
							// below, which drops the localIds the card anchors itself with.
							const isLastSuggestion = nextOrderedSuggestionIds.length === 0;
							const nextFollowUpCard: FollowUpCardState | undefined =
								isLastSuggestion && shouldShowFollowUpCard(suggestion)
									? {
											suggestionId: removedId,
											anchorLocalId: getAnchorLocalId(suggestion),
											baseLocalId: baseLocalIdBySuggestionId.get(removedId),
										}
									: undefined;

							suggestionById.delete(removedId);

							const baseLocalId = baseLocalIdBySuggestionId.get(removedId);
							baseLocalIdBySuggestionId.delete(removedId);

							if (baseLocalId) {
								suggestionIdsByBaseLocalId.get(baseLocalId)?.delete(removedId);
							}

							nextState = {
								...pluginState,
								suggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds: nextOrderedSuggestionIds,
								selectedSuggestion: undefined,
								followUpCard: nextFollowUpCard,
								decorations: calculateSuggestionsDecorations({
									doc: newState.doc,
									followUpCard: nextFollowUpCard,
									suggestionById,
									suggestionIdsByBaseLocalId,
									api,
									intl,
									selectedSuggestion: undefined,
									nodeViewPortalProviderAPI,
								}),
							};
							break;
						}
						case 'DISMISS_FOLLOW_UP_CARD': {
							if (!pluginState.followUpCard) {
								break;
							}
							nextState = {
								...pluginState,
								followUpCard: undefined,
								// Recalculated rather than mapped, so the card's anchor spans leave the DOM
								// along with it.
								decorations: suggestionsHidden
									? DecorationSet.empty
									: calculateSuggestionsDecorations({
											doc: newState.doc,
											followUpCard: undefined,
											suggestionById: pluginState.suggestionById,
											suggestionIdsByBaseLocalId: pluginState.suggestionIdsByBaseLocalId,
											api,
											intl,
											selectedSuggestion: pluginState.selectedSuggestion,
											nodeViewPortalProviderAPI,
										}),
							};
							break;
						}
						case 'HOVER_SUGGESTION': {
							const hoveredSuggestion = meta.id
								? pluginState.suggestionById.get(meta.id)
								: undefined;
							nextState = {
								...pluginState,
								hoveredSuggestionId: meta.id,
								hoverDecorations: suggestionsHidden
									? DecorationSet.empty
									: calculateHoverDecorations(newState.doc, hoveredSuggestion),
							};
							break;
						}
						default:
							break;
					}
				} else if (
					tr.docChanged &&
					getUndoRedoDirection(tr) === 'undo' &&
					pluginState.undoableAcceptedSuggestions.length > 0 &&
					getUndoDepthFromHistoryTr(tr) !== undefined &&
					(getUndoDepthFromHistoryTr(tr) as number) <
						pluginState.undoableAcceptedSuggestions[
							pluginState.undoableAcceptedSuggestions.length - 1
						].undoDepthAtAccept
				) {
					const restoredGroup =
						pluginState.undoableAcceptedSuggestions[
							pluginState.undoableAcceptedSuggestions.length - 1
						];
					const restoredGroups = [restoredGroup];
					const remainingUndoable = pluginState.undoableAcceptedSuggestions.slice(0, -1);
					const restoredIds = new Set(restoredGroup.suggestions.map(({ id }) => id));
					const nextSuggestions = [
						...getCurrentSuggestions(pluginState).filter(
							(suggestion) => !restoredIds.has(suggestion.id),
						),
						...restoredGroups.flatMap((group) => group.suggestions),
					];

					nextState = rebuildSuggestionsState({
						pluginState,
						suggestions: nextSuggestions,
						doc: newState.doc,
						suggestionsHidden,
						api,
						intl,
						nodeViewPortalProviderAPI,
					});

					nextState = {
						...nextState,
						undoableAcceptedSuggestions: remainingUndoable,
						redoableAcceptedSuggestions: [
							...pluginState.redoableAcceptedSuggestions,
							...restoredGroups,
						],
					};
				} else if (
					tr.docChanged &&
					getUndoRedoDirection(tr) === 'redo' &&
					pluginState.redoableAcceptedSuggestions.length > 0 &&
					getUndoDepthFromHistoryTr(tr) !== undefined &&
					(getUndoDepthFromHistoryTr(tr) as number) >=
						pluginState.redoableAcceptedSuggestions[
							pluginState.redoableAcceptedSuggestions.length - 1
						].undoDepthAtAccept
				) {
					const reAcceptedGroup =
						pluginState.redoableAcceptedSuggestions[
							pluginState.redoableAcceptedSuggestions.length - 1
						];
					const reAcceptedGroups = [reAcceptedGroup];
					const remainingRedoable = pluginState.redoableAcceptedSuggestions.slice(0, -1);
					const reAcceptedIds = new Set(reAcceptedGroup.suggestions.map(({ id }) => id));
					const nextSuggestions = getCurrentSuggestions(pluginState).filter(
						(suggestion) => !reAcceptedIds.has(suggestion.id),
					);

					nextState = rebuildSuggestionsState({
						pluginState,
						suggestions: nextSuggestions,
						doc: newState.doc,
						suggestionsHidden,
						api,
						intl,
						nodeViewPortalProviderAPI,
					});
					nextState = {
						...nextState,
						undoableAcceptedSuggestions: [
							...pluginState.undoableAcceptedSuggestions,
							...reAcceptedGroups,
						],
						redoableAcceptedSuggestions: remainingRedoable,
					};
				} else {
					const shouldRecalculateDecorations = docChangeAffectsDecorations({
						doc: _oldState.doc,
						suggestionIdsByBaseLocalId: pluginState.suggestionIdsByBaseLocalId,
						tr,
					});
					const shouldReconcileSuggestionAnchors =
						tr.docChanged &&
						(shouldRecalculateDecorations || getUndoRedoDirection(tr) !== undefined);
					const reconciledSuggestionMaps = shouldReconcileSuggestionAnchors
						? buildSuggestionMaps({
								doc: newState.doc,
								suggestions: [...pluginState.suggestionById.values()],
							})
						: undefined;
					const suggestionById =
						reconciledSuggestionMaps?.suggestionById ?? pluginState.suggestionById;
					const suggestionIdsByBaseLocalId =
						reconciledSuggestionMaps?.suggestionIdsByBaseLocalId ??
						pluginState.suggestionIdsByBaseLocalId;

					nextState = {
						...pluginState,
						...(reconciledSuggestionMaps ?? {}),
						hoverDecorations:
							suggestionsHidden || pluginState.hoverDecorations === DecorationSet.empty
								? DecorationSet.empty
								: pluginState.hoverDecorations.map(tr.mapping, tr.doc),
						selectedSuggestionDecorations:
							pluginState.selectedSuggestionDecorations === DecorationSet.empty
								? DecorationSet.empty
								: pluginState.selectedSuggestionDecorations.map(tr.mapping, tr.doc),
						remixPlaceholderDecorations:
							pluginState.remixPlaceholderDecorations === DecorationSet.empty
								? DecorationSet.empty
								: pluginState.remixPlaceholderDecorations.map(tr.mapping, tr.doc),
						decorations: suggestionsHidden
							? DecorationSet.empty
							: shouldRecalculateDecorations || shouldReconcileSuggestionAnchors
								? calculateSuggestionsDecorations({
										doc: newState.doc,
										followUpCard: pluginState.followUpCard,
										suggestionById,
										suggestionIdsByBaseLocalId,
										api,
										intl,
										selectedSuggestion: pluginState.selectedSuggestion,
										nodeViewPortalProviderAPI,
									})
								: pluginState.decorations.map(tr.mapping, tr.doc),
					};
				}

				if (nextState === pluginState) {
					return nextState;
				}

				const shouldRecalculateSelectionDecorations =
					Boolean(meta) || getUndoRedoDirection(tr) !== undefined;
				const selectedSuggestionDecorations = shouldRecalculateSelectionDecorations
					? calculateSelectedSuggestionDecorations(newState.doc, nextState, isViewMode(api))
					: nextState.selectedSuggestionDecorations;
				const remixPlaceholderDecorations = shouldRecalculateSelectionDecorations
					? calculateRemixPlaceholderDecorations(
							newState.doc,
							nextState,
							isViewMode(api),
							nodeViewPortalProviderAPI,
						)
					: nextState.remixPlaceholderDecorations;
				const activeHighlightDecorations =
					nextState.hoverDecorations === DecorationSet.empty
						? selectedSuggestionDecorations
						: nextState.hoverDecorations;

				// Merge the icon widgets with the active highlight and the remix placeholder
				// once here so the `decorations` prop returns a stable set. Reuse the
				// `decorations` reference (no allocation) when there is nothing extra.
				const extraDecorations = [
					...activeHighlightDecorations.find(),
					...remixPlaceholderDecorations.find(),
				];
				return {
					...nextState,
					selectedSuggestionDecorations,
					remixPlaceholderDecorations,
					mergedDecorations:
						extraDecorations.length === 0
							? nextState.decorations
							: nextState.decorations.add(newState.doc, extraDecorations),
				};
			},
		},
		/**
		 * The diff lives in the (separate) show-diff plugin, so it can't be reset from
		 * this plugin's `apply`. When the tracked `contentAri` changes — i.e. the
		 * suggestions now belong to a different page than the one whose diff is on
		 * screen — hide the diff so a stale diff doesn't linger on the new page (which
		 * can otherwise re-render whenever the new page contains a node with the same
		 * `localId`). The matching selection/decoration reset happens in the
		 * `SET_SUGGESTIONS` handler above.
		 *
		 * Also deselects the open suggestion once the content it is anchored to is gone —
		 * see below.
		 */
		appendTransaction(
			transactions: readonly Transaction[],
			oldState: EditorState,
			newState: EditorState,
		): Transaction | null {
			const invalidationIds = isExperimentEnabled('platform_editor_block_control_migration')
				? getSuggestionSurfaceInvalidationIds(transactions, oldState, newState)
				: [];
			const addSurfaceInvalidation = (tr: Transaction): Transaction => {
				if (invalidationIds.length > 0) {
					invalidateBlockControlsSurfaces(tr, { ids: invalidationIds });
				}
				return tr;
			};
			const previousContentAri = aiSuggestionsPluginKey.getState(oldState)?.contentAri;
			const nextState = aiSuggestionsPluginKey.getState(newState);
			const contentChanged =
				previousContentAri !== undefined &&
				nextState?.contentAri !== undefined &&
				previousContentAri !== nextState.contentAri;
			if (contentChanged) {
				const { tr } = newState;
				api?.showDiff?.commands.hideDiff?.({ tr });
				tr.setMeta(thinkingPluginKey, { status: 'idle' });
				return addSurfaceInvalidation(tr);
			}

			const isUndoRedo = transactions.some(
				(transaction) =>
					getUndoRedoDirection(transaction as unknown as ReadonlyTransaction) !== undefined,
			);
			if (isUndoRedo) {
				const previousPluginState = aiSuggestionsPluginKey.getState(oldState);

				const previousRedoableIds = collectSuggestionIds(
					previousPluginState?.redoableAcceptedSuggestions,
				);
				const nextRedoableIds = collectSuggestionIds(nextState?.redoableAcceptedSuggestions);

				// An undo moves an accept from the undo pool INTO the redo pool: the accept has been
				// reverted, so its suggestion goes back to PENDING. A redo moves it OUT of the redo pool
				// (back to the undo pool): the accept is re-applied, so its suggestion is ACTIONED again.
				const revertedToPendingIds = [...nextRedoableIds].filter(
					(id) => !previousRedoableIds.has(id),
				);
				const reAppliedToActionedIds = [...previousRedoableIds].filter(
					(id) => !nextRedoableIds.has(id),
				);

				if (revertedToPendingIds.length > 0) {
					updateSuggestionStatus({
						updates: revertedToPendingIds.map((id) => ({ id, status: 'PENDING' })),
					});
				}
				if (reAppliedToActionedIds.length > 0) {
					updateSuggestionStatus({
						updates: reAppliedToActionedIds.map((id) => ({ id, status: 'ACTIONED' })),
					});
				}

				const previousSelectedId = previousPluginState?.selectedSuggestion;
				const nextSelectedId = nextState?.selectedSuggestion;
				const selectionChanged = nextSelectedId !== previousSelectedId;

				// Surface refresh on undo/redo is handled in the view.update hook (post-DOM-flush); here we
				// only handle the open card's diff/selection.
				if (selectionChanged) {
					const { tr } = newState;
					const nextSelected = nextSelectedId
						? nextState?.suggestionById.get(nextSelectedId)
						: undefined;
					if (nextSelected) {
						applySuggestionDiff({ api, suggestion: nextSelected, tr });
						api?.userIntent?.commands.setCurrentUserIntent('reviewing')({ tr });
					} else {
						api?.showDiff?.commands.hideDiff?.({ tr });
						api?.userIntent?.commands.setCurrentUserIntent('default')({ tr });
					}
					tr.setMeta('addToHistory', false);
					return tr;
				}
			}

			/**
			 * If the content that the card is anchored to is deleted, the card is dangling
			 * Deselect it if the corresponding localId is no longer present in the doc.
			 */
			const shouldDeselect =
				nextState?.selectedSuggestion !== undefined &&
				transactions.some((transaction) => transaction.docChanged) &&
				!canPositionSelectedCard(newState.doc, nextState);
			if (shouldDeselect) {
				const { tr } = newState;
				tr.setMeta(aiSuggestionsPluginKey, { action: 'SELECT_SUGGESTION', id: undefined });
				api?.showDiff?.commands.hideDiff?.({ tr });
				api?.userIntent?.commands.setCurrentUserIntent('default')({ tr });
				return addSurfaceInvalidation(tr);
			}

			return invalidationIds.length > 0 ? addSurfaceInvalidation(newState.tr) : null;
		},
		props: {
			decorations(state: EditorState) {
				const pluginState = aiSuggestionsPluginKey.getState(state);
				return pluginState?.mergedDecorations ?? DecorationSet.empty;
			},
		},
		view: (editorView) => {
			const abortController = new AbortController();
			if (contentAri && api) {
				updateSuggestions({
					contentAri,
					api,
					signal: abortController.signal,
					/**
					 * getDoc is a function so it will get the most up to date
					 * doc when it eventually gets called when
					 * checking for staleness
					 */
					getDoc: () => editorView.state.doc,
					shouldRegenerate: true,
				});
			}

			// A view-mode change (edit ↔ view) isn't an ai-suggestions transaction, so the suggestion
			// decorations/diff won't otherwise refresh. Deselecting recomputes the decorations (empty
			// in view mode, restored in edit mode) and hides any open diff via the selectSuggestion
			// action — covering all surfaces without extra machinery.
			const unsubscribeViewMode = api?.editorViewMode?.sharedState.onChange?.(
				({ nextSharedState, prevSharedState }) => {
					if (nextSharedState?.mode === prevSharedState?.mode) {
						return;
					}
					api?.aiSuggestions?.actions.selectSuggestion(null);
				},
			);
			const unsubscribeAnnotation = UNSAFE_expValNoExposure(
				'confluence_suggested_edits_inline_comment_fix',
				'isEnabled',
				false,
			)
				? api?.annotation?.sharedState.onChange?.(({ nextSharedState }) => {
						const isInlineCommentOpen =
							!nextSharedState?.isInlineCommentViewClosed &&
							(nextSharedState?.isDrafting ||
								Boolean(nextSharedState?.selectedAnnotations?.length));
						const hasSelectedSuggestion = Boolean(
							aiSuggestionsPluginKey.getState(editorView.state)?.selectedSuggestion,
						);

						if (isInlineCommentOpen && hasSelectedSuggestion) {
							api?.aiSuggestions?.actions.selectSuggestion(null);
						}
					})
				: undefined;

			return {
				update: (view, prevState) => {
					// Remove the remix placeholder portal once, on the active → inactive transition.
					const wasActive = isSuggestedRemixCardActive(aiSuggestionsPluginKey.getState(prevState));
					const isActive = isSuggestedRemixCardActive(aiSuggestionsPluginKey.getState(view.state));
					if (wasActive && !isActive) {
						nodeViewPortalProviderAPI.remove(REMIX_PLACEHOLDER_WIDGET_KEY);
					}
				},
				destroy: () => {
					abortController.abort();
					unsubscribeViewMode?.();
					unsubscribeAnnotation?.();
					nodeViewPortalProviderAPI.remove(REMIX_PLACEHOLDER_WIDGET_KEY);
				},
			};
		},
	});
}
